"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, SunMedium, Snowflake, Compass } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Extreme Seasonality & Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Capitalize on high-compression periods—from the Tulip Festival and summer family holidays to winter snow transit—while stabilizing RevPAR across lean shoulder months with dynamic length-of-stay controls and advance purchase fences.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pricing & seasonal MLOS controls"
}, {
  id: 2,
  title: "Comp-Set Price Wars & Distressed Rates",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track aggressive rate dropping across Boulevard Road, Dal Lake, and Rajbagh comp-sets on MakeMyTrip, Booking.com, and Agoda. Intelligent rate fencing preserves your brand value while locking in high ADR bookings.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & dynamic rate fencing"
}, {
  id: 3,
  title: "Wholesaler Leakage & High OTA Fees",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Prevent tour operator and B2B wholesale rates from leaking onto unapproved OTAs. Enforce strict metasearch parity and capture direct booking intent from high-spending domestic and international leisure travelers.",
  metricLabel: "Strategy focus",
  metricValue: "Rate parity enforcement & direct channel growth"
}];
const seasonalTiers = [{
  season: "Peak Leisure (Apr – Jun)",
  desc: "Tulip garden bloom & summer surge. Focus on ADR maximization, strict minimum stays, and zero distress discounting.",
  icon: SunMedium
}, {
  season: "Autumn & Winter Transit (Sep – Feb)",
  desc: "Golden chinar foliage, winter getaways, and Gulmarg ski transit. Focus on package bundling and dynamic length-of-stay yields.",
  icon: Snowflake
}, {
  season: "Shoulder Recovery (Jul – Aug, Mar)",
  desc: "Post-monsoon and early spring. Focus on corporate retreats, MICE bookings, and value-add direct conversion campaigns.",
  icon: Compass
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapesrinagar" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Srinagar
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Srinagar presents a distinct hospitality micro-market shaped by acute seasonal demand swings, intense leisure surges, and heavy reliance on offline tour operators and OTAs. Sunil Singh structures disciplined, data-driven revenue management strategies engineered specifically for Srinagar hotels, resorts, and premium houseboats.
          </p>
        </div>

        {/* Seasonality Dynamics Overview Block */}
        <div className="mb-14 rounded-xl border border-border bg-card p-6 md:p-8 shadow-sm">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/70 pb-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                Demand Calendar
              </span>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground mt-0.5">
                Srinagar Seasonality & Yield Distribution
              </h3>
            </div>
            <span className="text-xs text-muted-foreground font-mono">
              Customized Yield Framework
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalTiers.map((tier, idx) => {
            const IconComp = tier.icon;
            return <div key={tier.season} data-index={idx} className="rounded-lg border border-border/60 bg-muted/30 p-5 flex flex-col justify-between">
                  <div>
                    <div className="inline-flex size-10 items-center justify-center rounded-md bg-secondary/15 text-secondary-foreground mb-3">
                      <IconComp className="size-5 text-secondary" />
                    </div>
                    <h4 className="font-semibold text-foreground text-base mb-1.5">
                      {tier.season}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {tier.desc}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-secondary" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-secondary" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-secondary" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Srinagar hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}